// FILE: src/Settings.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { GeoPoint, doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";

import { auth, db } from "./firebase";
import { geocodeAddress } from "./utils/geocodeAddress";
import useRestaurantContext from "./hooks/useRestaurantContext";
import ManagerShell from "./components/ManagerShell";
import StoreHours from "./pages/StoreHours";
import "./Dashboard.css";
import "./MenuManager.css";

const EMPTY_VALUES = {
  firstName: "",
  lastName: "",
  restaurantName: "",
  brandName: "",
  email: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  cuisineType: "",
  businessType: "",
};

const PROFILE_EMPTY_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  restaurantName: "",
  brandName: "",
  legalBusinessName: "",
  contactTitle: "",
  supportEmail: "",
  supportPhone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  cuisineType: "",
  businessType: "",
  foodPermitNumber: "",
  businessLicenceNumber: "",
  gstNumber: "",
};

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

const safeTrim = (v) => String(v ?? "").trim();
const digitsOnly = (v) => String(v ?? "").replace(/\D/g, "");
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeTrim(value));

const isAllowedDocumentType = (file) => {
  const type = String(file?.type || "").toLowerCase();
  return type === "application/pdf" || type.startsWith("image/");
};

const sanitizeFileName = (name) =>
  String(name || "document")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_");

const validateDocumentFile = (file, label) => {
  if (!file) return "";
  if (!isAllowedDocumentType(file)) {
    return `${label} must be a PDF or image file.`;
  }
  if (Number(file.size || 0) > MAX_UPLOAD_BYTES) {
    return `${label} must be under 15 MB.`;
  }
  return "";
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    if (typeof value.toDate === "function") {
      return value.toDate().toLocaleString();
    }
    if (value instanceof Date) {
      return value.toLocaleString();
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString();
    }
  } catch (err) {
    console.warn("[Settings] timestamp format failed:", err);
  }
  return "—";
};

const buildFormattedAddress = (values) =>
  [
    safeTrim(values.addressLine1),
    safeTrim(values.addressLine2),
    safeTrim(values.city),
    safeTrim(values.postalCode),
    "Canada",
  ]
    .filter(Boolean)
    .join(", ");

const uploadRestaurantDocument = async ({ restaurantId, docType, file }) => {
  const storage = getStorage();
  const ext = String(file?.name || "").includes(".")
    ? String(file.name).split(".").pop()
    : "";
  const stamp = Date.now();
  const safeName = sanitizeFileName(
    ext ? `${docType}-${stamp}.${ext}` : `${docType}-${stamp}`
  );
  const storagePath = `restaurants/${restaurantId}/documents/${docType}/${safeName}`;
  const fileRef = ref(storage, storagePath);

  await uploadBytes(fileRef, file, {
    contentType: file?.type || undefined,
  });

  const downloadUrl = await getDownloadURL(fileRef);

  return {
    documentName: file?.name || safeName,
    documentUrl: downloadUrl,
    storagePath,
    contentType: file?.type || "",
    size: Number(file?.size || 0),
  };
};

const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 16,
      padding: "10px 0",
      borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
      alignItems: "flex-start",
    }}
  >
    <div style={{ fontWeight: 700, color: "#0f172a", minWidth: 180 }}>{label}</div>
    <div style={{ color: "#475569", textAlign: "right", flex: 1 }}>{value || "—"}</div>
  </div>
);

const DocumentCard = ({ title, docData }) => {
  const verified = docData?.verified === true;
  const uploaded = docData?.uploaded === true;

  return (
    <div
      style={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 16,
        padding: 16,
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{title}</div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "6px 10px",
            borderRadius: 999,
            background: verified
              ? "rgba(22, 163, 74, 0.10)"
              : uploaded
              ? "rgba(245, 158, 11, 0.12)"
              : "rgba(148, 163, 184, 0.12)",
            color: verified ? "#166534" : uploaded ? "#b45309" : "#475569",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {verified ? "Verified" : uploaded ? "Pending review" : "Missing"}
        </span>
      </div>

      <DetailRow label="Number" value={docData?.number || "—"} />
      <DetailRow label="File name" value={docData?.documentName || "—"} />
      <DetailRow label="Uploaded" value={docData?.uploaded ? "Yes" : "No"} />
      <DetailRow label="Uploaded at" value={formatTimestamp(docData?.uploadedAt)} />
      <DetailRow label="Reviewed at" value={formatTimestamp(docData?.reviewedAt)} />
      <DetailRow label="Rejected reason" value={docData?.rejectionReason || "—"} />

      {docData?.documentUrl ? (
        <div style={{ marginTop: 14 }}>
          <a
            href={docData.documentUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none" }}
          >
            Open uploaded document
          </a>
        </div>
      ) : null}
    </div>
  );
};

const GeneralSettings = () => {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const successTimer = useRef(null);
  const { restaurantId, restaurant, loading: restaurantLoading } =
    useRestaurantContext() || {};

  // ✅ NEW: Main restaurant picture
  const [mainImageUrl, setMainImageUrl] = useState("");
  const [mainImageFile, setMainImageFile] = useState(null);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (!restaurantId) {
          setValues((prev) => ({
            ...prev,
            email: auth.currentUser?.email || "",
          }));
          setMainImageUrl("");
          return;
        }

        const data = restaurant;
        if (data) {
          const derivedFirst =
            data.firstName ||
            (data.contactName ? data.contactName.split(" ")[0] : "");
          const derivedLast =
            data.lastName ||
            (data.contactName
              ? data.contactName.split(" ").slice(1).join(" ")
              : "");

          setValues({
            ...EMPTY_VALUES,
            restaurantName: data.restaurantName || "",
            brandName: data.brandName || "",
            firstName: derivedFirst,
            lastName: derivedLast,
            email: data.email || auth.currentUser?.email || "",
            phone: data.phone || "",
            addressLine1: data.addressLine1 || data.address || "",
            addressLine2: data.addressLine2 || "",
            city: data.city || "",
            postalCode: data.postalCode || "",
            cuisineType: data.cuisineType || "",
            businessType: data.businessType || "",
          });

          // ✅ NEW: load existing main image
          setMainImageUrl(
            typeof data.mainImageUrl === "string" ? data.mainImageUrl : ""
          );
          return;
        }

        const docRef = doc(db, "restaurants", restaurantId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const snapData = snap.data();
          const derivedFirst =
            snapData.firstName ||
            (snapData.contactName ? snapData.contactName.split(" ")[0] : "");
          const derivedLast =
            snapData.lastName ||
            (snapData.contactName
              ? snapData.contactName.split(" ").slice(1).join(" ")
              : "");

          setValues({
            ...EMPTY_VALUES,
            restaurantName: snapData.restaurantName || "",
            brandName: snapData.brandName || "",
            firstName: derivedFirst,
            lastName: derivedLast,
            email: snapData.email || auth.currentUser?.email || "",
            phone: snapData.phone || "",
            addressLine1: snapData.addressLine1 || snapData.address || "",
            addressLine2: snapData.addressLine2 || "",
            city: snapData.city || "",
            postalCode: snapData.postalCode || "",
            cuisineType: snapData.cuisineType || "",
            businessType: snapData.businessType || "",
          });

          // ✅ NEW: load existing main image
          setMainImageUrl(
            typeof snapData.mainImageUrl === "string" ? snapData.mainImageUrl : ""
          );
        } else {
          setValues((prev) => ({
            ...prev,
            email: auth.currentUser?.email || "",
          }));
          setMainImageUrl("");
        }
      } catch (err) {
        console.error("Error loading settings:", err);
        setMessage("Failed to load settings");
      }
    };

    if (!restaurantLoading) {
      load();
    }

    return () => {
      if (successTimer.current) {
        clearTimeout(successTimer.current);
      }
    };
  }, [restaurantId, restaurantLoading, restaurant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ NEW: pick image
  const handleMainImagePick = (e) => {
    const file = e.target.files?.[0] || null;
    setMainImageFile(file);

    // Optional: preview immediately (local) — we keep Firestore url separate
    // We DO NOT set mainImageUrl to local blob so we don’t accidentally save it.
  };

  // ✅ NEW: upload to Firebase Storage + save URL to Firestore
  const handleUploadMainImage = async () => {
    try {
      setMessage("");

      if (restaurantLoading) throw new Error("Loading restaurant");
      if (!restaurantId) throw new Error("No logged-in user");
      if (!mainImageFile) {
        setMessage("Please choose an image first.");
        return;
      }

      setUploadingMainImage(true);

      const storage = getStorage();

      // keep paths clean + unique
      const safeName = `${Date.now()}-${mainImageFile.name || "main.jpg"}`.replace(
        /\s+/g,
        "-"
      );
      const storagePath = `restaurants/${restaurantId}/main/${safeName}`;

      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, mainImageFile);

      const url = await getDownloadURL(fileRef);

      // Save URL in Firestore
      const docRef = doc(db, "restaurants", restaurantId);
      await updateDoc(docRef, {
        mainImageUrl: url,
        mainImageUpdatedAt: Date.now(),
      });

      setMainImageUrl(url);
      setMainImageFile(null);

      setMessage("Main picture updated!");
      if (successTimer.current) clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setMessage(""), 2500);
    } catch (err) {
      console.error("Error uploading main picture:", err);
      setMessage("Failed to upload main picture.");
    } finally {
      setUploadingMainImage(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      if (restaurantLoading) {
        throw new Error("Loading restaurant");
      }
      if (!restaurantId) throw new Error("No logged-in user");

      const docRef = doc(db, "restaurants", restaurantId);
      const trimmed = Object.fromEntries(
        Object.entries(values).map(([key, val]) => [
          key,
          typeof val === "string" ? val.trim() : val,
        ])
      );
      const contactName = [trimmed.firstName, trimmed.lastName]
        .filter(Boolean)
        .join(" ");
      const formattedAddress = [
        trimmed.addressLine1,
        trimmed.addressLine2,
        trimmed.city,
        trimmed.postalCode,
        "Canada",
      ]
        .filter(Boolean)
        .join(", ");

      // Keep restaurant coordinates in sync when address changes so
      // customer-side distance calculations reflect the new location.
      let geocodedCoords = null;
      try {
        geocodedCoords = await geocodeAddress(formattedAddress);
      } catch (geocodeErr) {
        console.warn("[Settings] geocode failed, saving address text anyway:", geocodeErr);
      }

      const payload = {
        ...trimmed,
        email: trimmed.email.toLowerCase(),
        contactName,
        address: formattedAddress,
        fullAddress: formattedAddress,

        // ✅ keep main image url on the restaurant doc (if already set)
        ...(mainImageUrl ? { mainImageUrl } : {}),
      };

      if (
        geocodedCoords &&
        typeof geocodedCoords.lat === "number" &&
        typeof geocodedCoords.lng === "number"
      ) {
        payload.location = new GeoPoint(geocodedCoords.lat, geocodedCoords.lng);
        payload.lat = geocodedCoords.lat;
        payload.lng = geocodedCoords.lng;
      }

      await updateDoc(docRef, payload);

      setMessage(
        geocodedCoords
          ? "Saved!"
          : "Saved, but location coordinates could not be updated."
      );
      if (successTimer.current) {
        clearTimeout(successTimer.current);
      }
      successTimer.current = setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error saving settings:", err);
      setMessage("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (restaurantLoading) {
    return (
      <section className="dashboard-card">
        <h2 className="card-title">Restaurant settings</h2>
        <p className="status-message">Loading restaurant…</p>
      </section>
    );
  }

  if (!restaurantId) {
    return (
      <section className="dashboard-card">
        <h2 className="card-title">Restaurant settings</h2>
        <p className="status-message">
          No restaurant found. Please sign in again.
        </p>
      </section>
    );
  }

  return (
    <section className="dashboard-card">
      <h2 className="card-title">Restaurant settings</h2>

      {message && <p className="status-message">{message}</p>}

      <form className="settings-form" onSubmit={handleSave}>
        {/* ✅ NEW: Main Restaurant Picture */}
        <div className="form-row">
          <label>Main restaurant picture</label>

          {mainImageUrl ? (
            <div style={{ marginTop: 10 }}>
              <img
                src={mainImageUrl}
                alt="Main restaurant"
                style={{
                  width: "100%",
                  maxWidth: 520,
                  height: 220,
                  objectFit: "cover",
                  borderRadius: 14,
                  border: "1px solid #e5e7eb",
                }}
              />
            </div>
          ) : (
            <p className="status-message" style={{ marginTop: 6 }}>
              No main picture uploaded yet.
            </p>
          )}

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleMainImagePick}
              disabled={uploadingMainImage}
            />

            <button
              type="button"
              className="primary-btn"
              onClick={handleUploadMainImage}
              disabled={uploadingMainImage || !mainImageFile}
            >
              {uploadingMainImage ? "Uploading…" : "Upload picture"}
            </button>
          </div>

          <p className="status-message" style={{ marginTop: 8 }}>
            This picture will show in the Customer app restaurant header (Menu page).
          </p>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>First name</label>
            <input
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-row">
            <label>Last name</label>
            <input
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Restaurant name</label>
            <input
              name="restaurantName"
              value={values.restaurantName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-row">
            <label>Brand name</label>
            <input
              name="brandName"
              value={values.brandName}
              onChange={handleChange}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Email</label>
            <input
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              disabled
            />
          </div>
          <div className="form-row">
            <label>Phone</label>
            <input
              name="phone"
              value={values.phone}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <label>Address line 1</label>
          <input
            name="addressLine1"
            value={values.addressLine1}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-row">
          <label>Address line 2</label>
          <input
            name="addressLine2"
            value={values.addressLine2}
            onChange={handleChange}
            placeholder="Optional"
          />
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>City</label>
            <input
              name="city"
              value={values.city}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-row">
            <label>Postal code</label>
            <input
              name="postalCode"
              value={values.postalCode}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Cuisine type</label>
            <input
              name="cuisineType"
              value={values.cuisineType}
              onChange={handleChange}
            />
          </div>
          <div className="form-row">
            <label>Business type</label>
            <input
              name="businessType"
              value={values.businessType}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="primary-btn" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </section>
  );
};

const ProfileSettings = () => {
  const [values, setValues] = useState(PROFILE_EMPTY_VALUES);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [foodPermitFile, setFoodPermitFile] = useState(null);
  const [businessLicenceFile, setBusinessLicenceFile] = useState(null);
  const [foodPermitError, setFoodPermitError] = useState("");
  const [businessLicenceError, setBusinessLicenceError] = useState("");
  const successTimer = useRef(null);
  const { restaurantId, restaurant, loading: restaurantLoading } =
    useRestaurantContext() || {};

  useEffect(() => {
    const data = restaurant || {};
    const foodPermitDoc = data?.documents?.foodPermit || {};
    const businessLicenceDoc = data?.documents?.businessLicence || {};

    setValues({
      ...PROFILE_EMPTY_VALUES,
      firstName: data.firstName || "",
      lastName: data.lastName || "",
      email: data.email || auth.currentUser?.email || "",
      phone: data.phone || "",
      restaurantName: data.restaurantName || "",
      brandName: data.brandName || "",
      legalBusinessName: data.legalBusinessName || "",
      contactTitle: data.contactTitle || data.authorizedSignerTitle || "",
      supportEmail: data.supportEmail || "",
      supportPhone: data.supportPhone || "",
      addressLine1: data.addressLine1 || "",
      addressLine2: data.addressLine2 || "",
      city: data.city || "",
      postalCode: data.postalCode || "",
      cuisineType: data.cuisineType || "",
      businessType: data.businessType || "",
      foodPermitNumber:
        data.foodPermitNumber || foodPermitDoc.number || data?.compliance?.foodPermitNumber || "",
      businessLicenceNumber:
        data.businessLicenceNumber ||
        businessLicenceDoc.number ||
        data?.compliance?.businessLicenceNumber ||
        "",
      gstNumber: data.gstNumber || data?.compliance?.gstNumber || "",
    });
  }, [restaurant]);

  useEffect(() => {
    return () => {
      if (successTimer.current) {
        clearTimeout(successTimer.current);
      }
    };
  }, []);

  const profileSummary = useMemo(() => {
    const data = restaurant || {};
    const onboardingStatus = data.onboardingStatus || data.status || "unknown";
    const normalizedOnboardingStatus = String(onboardingStatus).trim().toLowerCase();

    const adminReviewStatus =
      data?.adminReview?.status ||
      (normalizedOnboardingStatus === "approved"
        ? "approved"
        : data?.approvalChecklist?.adminReviewed
        ? "reviewed"
        : data?.status === "approved"
        ? "approved"
        : "unknown");

    const supportContactsComplete =
      data.supportContactsComplete || data?.approvalChecklist?.supportContactsComplete;

    const emailVerifiedValue =
      data.emailVerifiedAt ||
      (data?.approvalChecklist?.emailVerified
        ? data.updatedAt || data.createdAt || data.submittedAt
        : auth.currentUser?.emailVerified
        ? "Verified"
        : null);

    const submittedValue = data.submittedAt || data.createdAt || data.updatedAt || null;

    return {
      onboardingStatus,
      adminReviewStatus,
      supportContactsComplete: supportContactsComplete ? "Yes" : "No",
      emailVerifiedAt:
        emailVerifiedValue === "Verified"
          ? "Yes"
          : formatTimestamp(emailVerifiedValue),
      submittedAt: formatTimestamp(submittedValue),
      updatedAt: formatTimestamp(data.updatedAt || data.createdAt),
    };
  }, [restaurant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDocumentPick = (type) => (e) => {
    const file = e.target?.files?.[0] || null;
    const label = type === "foodPermit" ? "Food permit" : "Business licence";
    const error = validateDocumentFile(file, label);

    if (type === "foodPermit") {
      setFoodPermitFile(error ? null : file);
      setFoodPermitError(error);
      return;
    }

    setBusinessLicenceFile(error ? null : file);
    setBusinessLicenceError(error);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      if (restaurantLoading) throw new Error("Loading restaurant");
      if (!restaurantId) throw new Error("No logged-in user");

      const trimmed = Object.fromEntries(
        Object.entries(values).map(([key, val]) => [key, typeof val === "string" ? val.trim() : val])
      );

      if (!safeTrim(trimmed.firstName) || !safeTrim(trimmed.lastName)) {
        throw new Error("Owner first and last name are required.");
      }

      if (!safeTrim(trimmed.restaurantName)) {
        throw new Error("Restaurant name is required.");
      }

      if (!safeTrim(trimmed.phone) || digitsOnly(trimmed.phone).length < 10) {
        throw new Error("Enter a valid phone number.");
      }

      if (safeTrim(trimmed.supportEmail) && !isValidEmail(trimmed.supportEmail)) {
        throw new Error("Enter a valid support email.");
      }

      if (safeTrim(trimmed.supportPhone) && digitsOnly(trimmed.supportPhone).length < 10) {
        throw new Error("Enter a valid support phone number.");
      }

      if (foodPermitError || businessLicenceError) {
        throw new Error(foodPermitError || businessLicenceError);
      }

      const docRef = doc(db, "restaurants", restaurantId);
      const contactName = [trimmed.firstName, trimmed.lastName].filter(Boolean).join(" ");
      const formattedAddress = buildFormattedAddress(trimmed);

      let geocodedCoords = null;
      try {
        geocodedCoords = await geocodeAddress(formattedAddress);
      } catch (geocodeErr) {
        console.warn("[ProfileSettings] geocode failed, saving address text anyway:", geocodeErr);
      }

      const payload = {
        firstName: trimmed.firstName,
        lastName: trimmed.lastName,
        contactName,
        contactTitle: trimmed.contactTitle,
        authorizedSignerName: contactName,
        authorizedSignerTitle: trimmed.contactTitle,
        email: (trimmed.email || auth.currentUser?.email || "").toLowerCase(),
        phone: trimmed.phone,
        restaurantName: trimmed.restaurantName,
        brandName: trimmed.brandName,
        legalBusinessName: trimmed.legalBusinessName,
        supportEmail: trimmed.supportEmail.toLowerCase(),
        supportPhone: trimmed.supportPhone,
        supportContactsComplete:
          Boolean(safeTrim(trimmed.supportEmail)) && Boolean(safeTrim(trimmed.supportPhone)),
        addressLine1: trimmed.addressLine1,
        addressLine2: trimmed.addressLine2,
        city: trimmed.city,
        postalCode: trimmed.postalCode,
        address: formattedAddress || trimmed.addressLine1,
        fullAddress: formattedAddress || trimmed.addressLine1,
        cuisineType: trimmed.cuisineType,
        businessType: trimmed.businessType,
        foodPermitNumber: trimmed.foodPermitNumber,
        businessLicenceNumber: trimmed.businessLicenceNumber,
        gstNumber: trimmed.gstNumber,
        "compliance.foodPermitNumber": trimmed.foodPermitNumber,
        "compliance.businessLicenceNumber": trimmed.businessLicenceNumber,
        "compliance.gstNumber": trimmed.gstNumber,
        "compliance.authorizedSignerName": contactName,
        "compliance.authorizedSignerTitle": trimmed.contactTitle,
        updatedAt: new Date(),
        "documents.foodPermit.number": trimmed.foodPermitNumber,
        "documents.businessLicence.number": trimmed.businessLicenceNumber,
      };

      if (
        geocodedCoords &&
        typeof geocodedCoords.lat === "number" &&
        typeof geocodedCoords.lng === "number"
      ) {
        payload.location = new GeoPoint(geocodedCoords.lat, geocodedCoords.lng);
        payload.lat = geocodedCoords.lat;
        payload.lng = geocodedCoords.lng;
      }

      if (foodPermitFile) {
        const uploaded = await uploadRestaurantDocument({
          restaurantId,
          docType: "foodPermit",
          file: foodPermitFile,
        });
        payload["documents.foodPermit.uploaded"] = true;
        payload["documents.foodPermit.verified"] = false;
        payload["documents.foodPermit.documentName"] = uploaded.documentName;
        payload["documents.foodPermit.documentUrl"] = uploaded.documentUrl;
        payload["documents.foodPermit.storagePath"] = uploaded.storagePath;
        payload["documents.foodPermit.contentType"] = uploaded.contentType;
        payload["documents.foodPermit.size"] = uploaded.size;
        payload["documents.foodPermit.uploadedAt"] = new Date();
        payload["documents.foodPermit.reviewedAt"] = null;
        payload["documents.foodPermit.reviewedBy"] = null;
        payload["documents.foodPermit.rejectionReason"] = "";
        payload["approvalChecklist.foodPermitUploaded"] = true;
        payload["approvalChecklist.foodPermitVerified"] = false;
        payload["compliance.docsPendingVerification"] = true;
      }

      if (businessLicenceFile) {
        const uploaded = await uploadRestaurantDocument({
          restaurantId,
          docType: "businessLicence",
          file: businessLicenceFile,
        });
        payload["documents.businessLicence.uploaded"] = true;
        payload["documents.businessLicence.verified"] = false;
        payload["documents.businessLicence.documentName"] = uploaded.documentName;
        payload["documents.businessLicence.documentUrl"] = uploaded.documentUrl;
        payload["documents.businessLicence.storagePath"] = uploaded.storagePath;
        payload["documents.businessLicence.contentType"] = uploaded.contentType;
        payload["documents.businessLicence.size"] = uploaded.size;
        payload["documents.businessLicence.uploadedAt"] = new Date();
        payload["documents.businessLicence.reviewedAt"] = null;
        payload["documents.businessLicence.reviewedBy"] = null;
        payload["documents.businessLicence.rejectionReason"] = "";
        payload["approvalChecklist.businessLicenceUploaded"] = true;
        payload["approvalChecklist.businessLicenceVerified"] = false;
        payload["compliance.docsPendingVerification"] = true;
      }

      await updateDoc(docRef, payload);

      setFoodPermitFile(null);
      setBusinessLicenceFile(null);
      setMessage("Profile updated!");
      if (successTimer.current) clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error saving profile settings:", err);
      setMessage(err?.message || "Failed to save profile settings.");
    } finally {
      setSaving(false);
    }
  };

  if (restaurantLoading) {
    return (
      <section className="dashboard-card">
        <h2 className="card-title">Profile & onboarding details</h2>
        <p className="status-message">Loading profile…</p>
      </section>
    );
  }

  if (!restaurantId) {
    return (
      <section className="dashboard-card">
        <h2 className="card-title">Profile & onboarding details</h2>
        <p className="status-message">No restaurant found. Please sign in again.</p>
      </section>
    );
  }

  return (
    <section className="dashboard-card">
      <h2 className="card-title">Profile & onboarding details</h2>
      <p className="status-message" style={{ marginTop: 4 }}>
        Review and update the business, owner, support, and compliance details submitted during onboarding.
      </p>

      {message ? <p className="status-message">{message}</p> : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 16,
          marginTop: 18,
          marginBottom: 22,
        }}
      >
        <div
          style={{
            border: "1px solid rgba(15, 23, 42, 0.08)",
            borderRadius: 16,
            padding: 16,
            background: "#fff",
          }}
        >
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Account status</div>
          <DetailRow label="Onboarding" value={profileSummary.onboardingStatus} />
          <DetailRow label="Admin review" value={profileSummary.adminReviewStatus} />
          <DetailRow label="Support contacts" value={profileSummary.supportContactsComplete} />
          <DetailRow label="Email verified" value={profileSummary.emailVerifiedAt} />
          <DetailRow label="Submitted" value={profileSummary.submittedAt} />
          <DetailRow label="Last updated" value={profileSummary.updatedAt} />
        </div>

        <div
          style={{
            border: "1px solid rgba(15, 23, 42, 0.08)",
            borderRadius: 16,
            padding: 16,
            background: "#fff",
          }}
        >
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Current document status</div>
          <DetailRow
            label="Food permit"
            value={restaurant?.documents?.foodPermit?.verified ? "Verified" : restaurant?.documents?.foodPermit?.uploaded ? "Pending review" : "Missing"}
          />
          <DetailRow
            label="Business licence"
            value={restaurant?.documents?.businessLicence?.verified ? "Verified" : restaurant?.documents?.businessLicence?.uploaded ? "Pending review" : "Missing"}
          />
          <DetailRow
            label="GST number"
            value={restaurant?.gstNumber || restaurant?.compliance?.gstNumber || "—"}
          />
        </div>
      </div>

      <form className="settings-form" onSubmit={handleSave}>
        <div className="form-row-grid">
          <div className="form-row">
            <label>Owner first name</label>
            <input name="firstName" value={values.firstName} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <label>Owner last name</label>
            <input name="lastName" value={values.lastName} onChange={handleChange} required />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Owner title</label>
            <input name="contactTitle" value={values.contactTitle} onChange={handleChange} />
          </div>
          <div className="form-row">
            <label>Account email</label>
            <input name="email" type="email" value={values.email} onChange={handleChange} disabled />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Phone</label>
            <input name="phone" value={values.phone} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <label>Support phone</label>
            <input name="supportPhone" value={values.supportPhone} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Restaurant name</label>
            <input name="restaurantName" value={values.restaurantName} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <label>Brand name</label>
            <input name="brandName" value={values.brandName} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Legal business name</label>
            <input name="legalBusinessName" value={values.legalBusinessName} onChange={handleChange} />
          </div>
          <div className="form-row">
            <label>Support email</label>
            <input name="supportEmail" type="email" value={values.supportEmail} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <label>Address line 1</label>
          <input name="addressLine1" value={values.addressLine1} onChange={handleChange} required />
        </div>

        <div className="form-row">
          <label>Address line 2</label>
          <input name="addressLine2" value={values.addressLine2} onChange={handleChange} placeholder="Optional" />
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>City</label>
            <input name="city" value={values.city} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <label>Postal code</label>
            <input name="postalCode" value={values.postalCode} onChange={handleChange} required />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Cuisine type</label>
            <input name="cuisineType" value={values.cuisineType} onChange={handleChange} />
          </div>
          <div className="form-row">
            <label>Business type</label>
            <input name="businessType" value={values.businessType} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Food permit number</label>
            <input name="foodPermitNumber" value={values.foodPermitNumber} onChange={handleChange} />
          </div>
          <div className="form-row">
            <label>Business licence number</label>
            <input name="businessLicenceNumber" value={values.businessLicenceNumber} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <label>GST number</label>
          <input name="gstNumber" value={values.gstNumber} onChange={handleChange} placeholder="Optional" />
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>Replace food permit document</label>
            <input type="file" accept="application/pdf,image/*" onChange={handleDocumentPick("foodPermit")} />
            {foodPermitError ? <p className="status-message">{foodPermitError}</p> : null}
          </div>
          <div className="form-row">
            <label>Replace business licence document</label>
            <input type="file" accept="application/pdf,image/*" onChange={handleDocumentPick("businessLicence")} />
            {businessLicenceError ? <p className="status-message">{businessLicenceError}</p> : null}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="primary-btn" disabled={saving}>
            {saving ? "Saving…" : "Save profile details"}
          </button>
        </div>
      </form>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 16,
          marginTop: 24,
        }}
      >
        <DocumentCard title="Food permit" docData={restaurant?.documents?.foodPermit} />
        <DocumentCard title="Business licence" docData={restaurant?.documents?.businessLicence} />
      </div>
    </section>
  );
};

const PasswordSettings = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error("No signed-in user found. Please sign in again.");
      }

      if (!safeTrim(currentPassword)) {
        throw new Error("Current password is required.");
      }

      if (safeTrim(newPassword).length < 6) {
        throw new Error("New password must be at least 6 characters.");
      }

      if (newPassword !== confirmPassword) {
        throw new Error("New password and confirm password must match.");
      }

      if (currentPassword === newPassword) {
        throw new Error("New password must be different from your current password.");
      }

      setSaving(true);

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage("Password updated successfully.");
    } catch (err) {
      console.error("Error updating password:", err);
      setMessage(err?.message || "Failed to update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="dashboard-card">
      <h2 className="card-title">Change password</h2>
      <p className="status-message" style={{ marginTop: 4 }}>
        Update the password used to sign in to this restaurant portal account.
      </p>

      {message ? <p className="status-message">{message}</p> : null}

      <form className="settings-form" onSubmit={handlePasswordChange}>
        <div className="form-row">
          <label>Current password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>

        <div className="form-row-grid">
          <div className="form-row">
            <label>New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label>Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="primary-btn" disabled={saving}>
            {saving ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </section>
  );
};

const SETTINGS_TABS = [
  { key: "general", label: "General", path: "/settings/general" },
  { key: "profile", label: "Profile", path: "/settings/profile" },
  { key: "password", label: "Password", path: "/settings/password" },
  { key: "store-hours", label: "Store Hours", path: "/settings/store-hours" },
];

const Settings = () => (
  <ManagerShell
    title="Settings"
    subtitle="Keep your restaurant details up to date."
  >
    <div className="menu-page">
      <div className="menu-tabs">
        {SETTINGS_TABS.map((tab) => (
          <NavLink
            key={tab.key}
            to={tab.path}
            className={({ isActive }) =>
              `menu-tab ${isActive ? "menu-tab--active" : ""}`
            }
            end={tab.path === "/settings/general"}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<Navigate to="/settings/general" replace />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route path="profile" element={<ProfileSettings />} />
        <Route path="password" element={<PasswordSettings />} />
        <Route path="store-hours" element={<StoreHours />} />
        <Route path="*" element={<Navigate to="/settings/general" replace />} />
      </Routes>
    </div>
  </ManagerShell>
);

export default Settings;
